// Shopify owns cart updates and checkout. This module only enhances quantities and shipping estimates.
const unavailable = 'Unable to estimate shipping. Please try again or view shipping options at checkout.';
const delayed = 'Shipping rates are taking longer than expected. Please try again or continue to checkout.';

export async function loadShippingRates(cartUrl, address, {fetch: request = globalThis.fetch, maxAttempts = 8, pollDelay = 1000, timeout = 15000, signal} = {}) {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener('abort', cancel, {once: true});
  if (signal?.aborted) cancel();
  const timer = setTimeout(cancel, timeout);
  const params = new URLSearchParams();
  for (const key of ['country', 'province', 'zip']) params.set(`shipping_address[${key}]`, address[key] || '');
  const base = cartUrl.replace(/\/$/, '');
  async function read(endpoint, method) {
    controller.signal.throwIfAborted();
    const response = await request(`${base}/${endpoint}.json?${params}`, {method, credentials: 'same-origin', headers: {Accept: 'application/json'}, signal: controller.signal});
    let data;
    try { data = await response.json(); } catch { throw new Error(unavailable); }
    controller.signal.throwIfAborted();
    if (!response.ok) {
      const errors = data?.errors || data?.description || data;
      const message = typeof errors === 'string' ? errors : Object.values(errors || {}).flat().filter(value => typeof value === 'string').join(' ');
      throw new Error(message || unavailable);
    }
    return data;
  }
  try {
    await read('prepare_shipping_rates', 'POST');
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt) await new Promise(resolve => setTimeout(resolve, pollDelay));
      const data = await read('async_shipping_rates', 'GET');
      if (data === null || data?.shipping_rates === null) continue;
      if (!Array.isArray(data?.shipping_rates)) throw new Error(unavailable);
      return data.shipping_rates;
    }
    throw new Error(delayed);
  } catch (error) {
    if (controller.signal.aborted) throw new Error(delayed);
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

export function initializeCart(root, view = window) {
  const form = root.querySelector('#RenoCartForm');
  if (!form) return {disconnect() {}};
  const quantities = [...form.querySelectorAll('[name="updates[]"]')];
  const dirtyMessage = root.querySelector('[data-cart-dirty-message]');
  const terms = form.querySelector('[data-cart-terms]');
  const additional = form.querySelector('[data-additional-checkout]');
  const shipping = root.querySelector('[data-cart-shipping]');
  const button = shipping?.querySelector('button[type="submit"]');
  const status = shipping?.querySelector('[data-shipping-status]');
  const rates = shipping?.querySelector('[data-shipping-rates]');
  let dirty = false;
  let busy = false;
  let generation = 0;
  let currentRequest;
  const listeners = [];
  function on(element, type, listener) {
    if (!element) return;
    element.addEventListener(type, listener);
    listeners.push(() => element.removeEventListener(type, listener));
  }
  function refresh() {
    dirty = quantities.some(input => input.value !== input.defaultValue || !input.validity.valid);
    root.dataset.cartDirty = String(dirty);
    if (dirtyMessage) dirtyMessage.hidden = !dirty;
    if (button) button.disabled = dirty || busy;
    if (additional) additional.hidden = dirty || Boolean(terms && !terms.checked);
  }
  function invalidate() {
    generation++;
    currentRequest?.abort();
    currentRequest = undefined;
    busy = false;
    shipping?.removeAttribute('aria-busy');
    rates?.replaceChildren();
    if (status) {status.textContent = ''; delete status.dataset.state;}
    refresh();
    if (dirty && status) status.textContent = 'Update your cart before estimating shipping.';
  }
  on(form, 'input', event => {if (event.target.matches('[name="updates[]"]')) invalidate();});
  on(form, 'change', event => {if (event.target.matches('[name="updates[]"]')) invalidate();});
  on(terms, 'change', refresh);
  on(form, 'submit', event => {
    // Update skips checkout-only terms; retain native quantity feedback before its POST.
    if (event.submitter?.name !== 'update' || !event.submitter.formNoValidate) return;
    const invalid = quantities.find(input => !input.validity.valid);
    if (invalid) {event.preventDefault(); invalid.focus(); invalid.reportValidity();}
  });
  on(form, 'click', event => {
    const control = event.target.closest('[data-cart-step]');
    if (!control || !form.contains(control)) return;
    const input = control.closest('[data-cart-line]')?.querySelector('[name="updates[]"]');
    if (!input) return;
    if (control.dataset.cartStep === '1') input.stepUp(); else input.stepDown();
    input.dispatchEvent(new view.Event('input', {bubbles: true}));
  });
  for (const control of form.querySelectorAll('[data-cart-step]')) control.hidden = false;
  root.dataset.enhanced = 'true';

  if (shipping) {
    const country = shipping.elements.namedItem('country');
    const province = shipping.elements.namedItem('province');
    const zip = shipping.elements.namedItem('zip');
    function updateProvinces(useDefault = false) {
      let options = [];
      try { options = JSON.parse(country.options[country.selectedIndex]?.dataset.provinces || '[]'); } catch { /* A country without province data has no province field. */ }
      province.replaceChildren();
      for (const [value, label] of options) {
        const option = view.document.createElement('option');
        option.value = value; option.textContent = label; province.append(option);
      }
      province.disabled = options.length === 0;
      shipping.querySelector('[data-shipping-province]').hidden = province.disabled;
      if (useDefault && [...province.options].some(option => option.value === province.dataset.default)) province.value = province.dataset.default;
    }
    if ([...country.options].some(option => option.value === country.dataset.default)) country.value = country.dataset.default;
    updateProvinces(true);
    on(country, 'change', () => {updateProvinces(); invalidate();});
    on(province, 'change', invalidate);
    on(zip, 'input', invalidate);
    on(shipping, 'submit', async event => {
      event.preventDefault();
      refresh();
      if (dirty) {invalidate(); return;}
      if (busy || !shipping.reportValidity()) return;
      const requestGeneration = ++generation;
      currentRequest = new AbortController();
      busy = true; refresh(); rates.replaceChildren();
      status.textContent = 'Estimating shipping…'; delete status.dataset.state;
      shipping.setAttribute('aria-busy', 'true');
      try {
        const result = await loadShippingRates(shipping.dataset.cartUrl, {country: country.value, province: province.disabled ? '' : province.value, zip: zip.value.trim()}, {fetch: (...args) => view.fetch(...args), signal: currentRequest.signal});
        if (requestGeneration !== generation) return;
        for (const rate of result) {
          const line = view.document.createElement('li');
          const price = Number(rate.price);
          let formatted = rate.price;
          if (Number.isFinite(price)) formatted = new Intl.NumberFormat(shipping.dataset.locale || 'en', {style: 'currency', currency: rate.currency || shipping.dataset.currency}).format(price);
          line.textContent = `${rate.name}: ${formatted}`;
          rates.append(line);
        }
        status.textContent = result.length ? 'Estimated rates. Final options are confirmed at checkout.' : 'No shipping rates were returned for this address. Check your details or view options at checkout.';
      } catch (error) {
        if (requestGeneration !== generation) return;
        status.textContent = error.message || unavailable;
        status.dataset.state = 'error';
      } finally {
        if (requestGeneration === generation) {
          busy = false; currentRequest = undefined; refresh();
          shipping.removeAttribute('aria-busy');
        }
      }
    });
  }
  refresh();
  return {disconnect() {generation++; currentRequest?.abort(); for (const remove of listeners) remove();}};
}

if (typeof document !== 'undefined') {
  for (const root of document.querySelectorAll('[data-reno-cart]')) initializeCart(root);
}
